import { useState, type FormEvent } from 'react';
import { t } from '../i18n';
import { AuthFailure, type ConsoleApi } from '../lib/api';
import { usePageTitle } from '../shell/page-title';
import { controlClass } from '../styles';
import { Button, CenteredPage, Field, Notice } from '../ui';
import { PasswordInput } from './password-field';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** password: the usual way in. link and reset send an email (limited per hour). */
type Mode = 'password' | 'link' | 'reset';
type FormError =
  'invalid_email' | 'missing_password' | 'invalid_credentials' | 'rate_limited' | 'failed';

type State =
  | { readonly step: 'form'; readonly error: FormError | null }
  | { readonly step: 'busy' }
  | { readonly step: 'sent'; readonly email: string };

const headingClass = 'text-[28px] leading-tight font-semibold tracking-[-0.03em] outline-none';
const linkButtonClass =
  'min-h-11 justify-self-start rounded-lg py-2 text-left text-[14.5px] font-medium text-muted underline underline-offset-4 hover:text-ink';

export function LoginScreen({ api }: { api: ConsoleApi }) {
  const [mode, setMode] = useState<Mode>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [state, setState] = useState<State>({ step: 'form', error: null });
  const title =
    state.step === 'sent'
      ? t('login.sent.title')
      : t(mode === 'reset' ? 'login.reset.title' : 'login.title');
  const headingRef = usePageTitle(title);

  function switchTo(next: Mode) {
    setMode(next);
    setPassword('');
    setShowPassword(false);
    setState({ step: 'form', error: null });
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setShowPassword(false);
    const address = email.trim().toLowerCase();
    if (!EMAIL.test(address)) {
      setState({ step: 'form', error: 'invalid_email' });
      return;
    }
    if (mode === 'password' && !password) {
      setState({ step: 'form', error: 'missing_password' });
      return;
    }
    setState({ step: 'busy' });
    const back = window.location.origin + window.location.pathname;
    try {
      if (mode === 'password') {
        await api.signInWithPassword(address, password);
        return; // the session change takes it from here
      }
      if (mode === 'link') await api.sendSignInLink(address, back);
      // The reset link lands on "Tu cuenta" even if the recovery event comes too early.
      else await api.sendPasswordReset(address, `${back}#/cuenta`);
      setState({ step: 'sent', email: address });
    } catch (error) {
      const reason = error instanceof AuthFailure ? error.reason : 'failed';
      setState({
        step: 'form',
        error: reason === 'invalid_credentials' || reason === 'rate_limited' ? reason : 'failed',
      });
    }
  }

  if (state.step === 'sent') {
    return (
      <CenteredPage>
        <div className="grid gap-2">
          <h1 ref={headingRef} tabIndex={-1} className={headingClass}>
            {t('login.sent.title')}
          </h1>
          <p className="text-muted">
            {t(mode === 'reset' ? 'login.reset.sent' : 'login.sent.body', { email: state.email })}
          </p>
          <p className="text-sm text-muted">{t('login.sent.limit')}</p>
        </div>
        <Button className="justify-self-start" onClick={() => switchTo('password')}>
          {t('login.backToPassword')}
        </Button>
      </CenteredPage>
    );
  }

  const error = state.step === 'form' ? state.error : null;
  const busy = state.step === 'busy';
  const subtitle = t(`login.subtitle.${mode}`);
  const submitLabel = busy
    ? t(mode === 'password' ? 'login.signingIn' : 'login.sending')
    : t(`login.submit.${mode}`);

  return (
    <CenteredPage>
      <div className="grid gap-2">
        <h1 ref={headingRef} tabIndex={-1} className={headingClass}>
          {title}
        </h1>
        <p className="text-muted">{subtitle}</p>
      </div>
      <form className="grid gap-5" onSubmit={(event) => void submit(event)} noValidate>
        <Field label={t('login.email')} htmlFor="login-email">
          <input
            id="login-email"
            type="email"
            autoComplete="username"
            inputMode="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            aria-invalid={error === 'invalid_email'}
            aria-describedby={error ? 'login-error' : undefined}
            className={controlClass}
          />
        </Field>
        {mode === 'password' ? (
          <Field label={t('login.password')} htmlFor="login-password">
            <PasswordInput
              id="login-password"
              autoComplete="current-password"
              value={password}
              onChange={setPassword}
              visible={showPassword}
              onVisibleChange={setShowPassword}
              invalid={error === 'missing_password' || error === 'invalid_credentials'}
              describedBy={error ? 'login-error' : undefined}
            />
          </Field>
        ) : null}
        {error ? <Notice id="login-error">{t(`login.error.${error}`)}</Notice> : null}
        {/* Sign-in pages only: a full-width main action in the narrow card. */}
        <Button type="submit" variant="primary" disabled={busy} className="w-full">
          {submitLabel}
        </Button>
      </form>
      <div className="-mb-2 grid border-t border-line pt-3">
        {mode === 'password' ? (
          <>
            <button type="button" className={linkButtonClass} onClick={() => switchTo('reset')}>
              {t('login.toReset')}
            </button>
            <button type="button" className={linkButtonClass} onClick={() => switchTo('link')}>
              {t('login.toLink')}
            </button>
          </>
        ) : (
          <button type="button" className={linkButtonClass} onClick={() => switchTo('password')}>
            {t('login.backToPassword')}
          </button>
        )}
      </div>
    </CenteredPage>
  );
}
